import { callable } from './callable'
import type { TimelineBoardKey } from '../types/schema'

export type RestructureOp =
  | 'bin'
  | 'restore'
  | 'move'
  | 'reparent'
  | 'unarchive'
  | 'deleteForever'
  | 'moveChecklist'
  | 'archiveChecklist'
  | 'unarchiveChecklist'
  | 'binChecklist'
  | 'restoreChecklist'
  | 'deleteChecklistForever'

export interface RestructureElementsInput {
  workspaceId: string
  op: RestructureOp
  rootIds: string[]
  targetBoard?: TimelineBoardKey
  targetParentId?: string | null
}

export interface RestructureElementsResult {
  success: boolean
  op: RestructureOp
  affectedElementIds: string[]
  affectedChecklistIds: string[]
}

export async function restructureElements(
  input: RestructureElementsInput,
): Promise<RestructureElementsResult> {
  return callable<RestructureElementsInput, RestructureElementsResult>(
    'restructureElements',
    input,
  )
}

export async function binElements(
  workspaceId: string,
  rootIds: string[],
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'bin',
    rootIds,
  })
}

export async function restoreElements(
  workspaceId: string,
  rootIds: string[],
  targetBoard?: TimelineBoardKey,
  targetParentId?: string | null,
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'restore',
    rootIds,
    targetBoard,
    targetParentId,
  })
}

export async function moveElements(
  workspaceId: string,
  rootIds: string[],
  targetBoard: TimelineBoardKey,
  targetParentId?: string | null,
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'move',
    rootIds,
    targetBoard,
    targetParentId,
  })
}

export async function reparentElements(
  workspaceId: string,
  rootIds: string[],
  targetParentId: string | null,
  targetBoard?: TimelineBoardKey,
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'reparent',
    rootIds,
    targetParentId,
    targetBoard,
  })
}

export async function unarchiveElements(
  workspaceId: string,
  rootIds: string[],
  targetBoard?: TimelineBoardKey,
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'unarchive',
    rootIds,
    targetBoard,
  })
}

export async function deleteForeverElements(
  workspaceId: string,
  rootIds: string[],
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'deleteForever',
    rootIds,
  })
}

export async function moveChecklist(
  workspaceId: string,
  rootIds: string[],
  targetBoard?: TimelineBoardKey,
  targetParentId?: string | null,
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'moveChecklist',
    rootIds,
    targetBoard,
    targetParentId,
  })
}

export async function archiveChecklist(
  workspaceId: string,
  rootIds: string[],
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'archiveChecklist',
    rootIds,
  })
}

export async function unarchiveChecklist(
  workspaceId: string,
  rootIds: string[],
  targetBoard?: TimelineBoardKey,
  targetParentId?: string | null,
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'unarchiveChecklist',
    rootIds,
    targetBoard,
    targetParentId,
  })
}

export async function binChecklist(
  workspaceId: string,
  rootIds: string[],
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'binChecklist',
    rootIds,
  })
}

export async function restoreChecklist(
  workspaceId: string,
  rootIds: string[],
  targetBoard?: TimelineBoardKey,
  targetParentId?: string | null,
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'restoreChecklist',
    rootIds,
    targetBoard,
    targetParentId,
  })
}

export async function deleteChecklistForever(
  workspaceId: string,
  rootIds: string[],
): Promise<RestructureElementsResult> {
  return restructureElements({
    workspaceId,
    op: 'deleteChecklistForever',
    rootIds,
  })
}
